/**
 * OfflineManagementPage.tsx
 *
 * 離線管理頁面組件（Task 59）。
 *
 * 功能：
 * - 顯示當前設備指紋與持久化存儲狀態（可一鍵啟用）
 * - 顯示同步狀態（在線/離線、待同步條目、上次同步時間）
 * - 提供手動全量同步、本地備份導出、設備釋放功能
 * - iOS 用戶 PWA 安裝提醒（防止 Safari 7 天自動清理 IndexedDB）
 *
 * 路由：由 Task 60 負責接入 TanStack Router。
 *
 * 使用範例：
 * ```tsx
 * // 在路由組件中直接渲染
 * <OfflineManagementPage />
 * ```
 */

import { useEffect, useState } from 'react';
import { Card, Button, Descriptions, Space, Tag, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import { useOfflineState } from '@/pwa/use-offline-state';
import { ensurePersistentStorage, getStorageStatus, type PersistStatus } from '@/pwa/persist';
import { getDeviceFingerprint } from '@/pwa/fingerprint';
import { apiClient } from '@/lib/api-client';
import { getOfflineDb } from '@/pwa/db';
import { OfflineTaskPackList } from '@/components/notifications/OfflineTaskPackList';
import { FailedOutboxList } from '@/components/notifications/FailedOutboxList';

const { Title, Paragraph } = Typography;

/**
 * 離線存儲 / 同步管理頁面（在線後台用，需登入）。
 * 集中展示設備狀態、存儲配額、同步進度，並提供管理操作。
 * 已下載任務包列表復用 `OfflineTaskPackList`。
 *
 * 離線現場的極簡入口已獨立為 `(public)/me/offline.tsx`，與本頁無關。
 */
export function OfflineManagementPage() {
  const { online, pending, failed, syncing, lastSyncAt, triggerSync, refreshCounts } =
    useOfflineState();
  const [persist, setPersist] = useState<PersistStatus>({ persisted: false, quotaPct: null });
  const [fingerprint, setFingerprint] = useState<string>('');

  useEffect(() => {
    // 頁面掛載時並行初始化設備指紋和存儲狀態
    void getStorageStatus().then(setPersist);
    void getDeviceFingerprint().then(setFingerprint);
  }, []);

  /**
   * 申請持久化存儲權限。
   * Chrome 通常需要網站在書籤或已安裝為 PWA 時才會自動授予。
   */
  async function requestPersist() {
    const ok = await ensurePersistentStorage();
    if (ok) {
      message.success('已啟用持久化存儲');
    } else {
      message.warning('瀏覽器拒絕了持久化請求，請嘗試將應用加入書籤或安裝為 PWA');
    }
    setPersist(await getStorageStatus());
  }

  /**
   * 確認並釋放當前設備的 checkout。
   * 釋放後服務端不再保留心跳，同一 user 可在其他設備重新 checkout。
   */
  function releaseDevice() {
    modal.confirm({
      title: '確定釋放當前設備？',
      content: '釋放後本機所有未同步資料將不再被服務端保留心跳，請確認已完成同步後再釋放。',
      okText: '確定釋放',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiClient.post('/api/sync/release', { deviceFingerprint: fingerprint });
          message.success('已釋放設備');
        } catch (err) {
          // 釋放失敗時提示，不靜默吞錯
          const msg = err instanceof Error ? err.message : String(err);
          message.error(`釋放失敗：${msg}`);
        }
      },
    });
  }

  /**
   * 將本地 IndexedDB 資料導出為 JSON 備份文件。
   * 備份包含 outbox、案件緩存、表單緩存（媒體文件僅記錄數量）。
   */
  async function exportBackup() {
    try {
      const db = await getOfflineDb();
      const [outbox, cases, forms, media] = await Promise.all([
        db.getAll('outbox'),
        db.getAll('cache_cases'),
        db.getAll('cache_forms'),
        db.getAll('cache_media'),
      ]);

      const blob = new Blob(
        [
          JSON.stringify(
            {
              outbox,
              cases,
              forms,
              mediaCount: media.length,
              exportedAt: Date.now(),
              deviceFingerprint: fingerprint,
            },
            null,
            2,
          ),
        ],
        { type: 'application/json' },
      );

      // 觸發瀏覽器下載
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fingerprint.slice(0, 8)}-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('備份文件已導出');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      message.error(`導出失敗：${msg}`);
    }
  }

  // 離線時退化為僅顯示已下載任務包（管理操作需在線）
  if (!online) {
    return (
      <div className="p-2 md:p-4">
        <Title level={3}>離線任務</Title>
        <OfflineTaskPackList allowDelete={false} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Title level={3}>離線管理</Title>

      <Title level={5}>已下載任務</Title>
      <div className="mb-4">
        <OfflineTaskPackList allowDelete />
      </div>

      {/* ── 設備信息卡片 ── */}
      <Card title="當前設備" className="mb-4">
        <Descriptions column={1}>
          <Descriptions.Item label="設備指紋">{fingerprint || '計算中…'}</Descriptions.Item>

          <Descriptions.Item label="持久化存儲">
            <Tag color={persist.persisted ? 'green' : 'red'}>
              {persist.persisted ? '已啟用' : '未啟用（瀏覽器可能自動清理數據）'}
            </Tag>
            {!persist.persisted && (
              <Button size="small" type="primary" onClick={requestPersist} className="ml-2">
                立即啟用
              </Button>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="存儲使用率">
            {persist.quotaPct != null ? `${persist.quotaPct.toFixed(1)}%` : '未知'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ── 同步狀態卡片 ── */}
      <Card title="同步狀態" className="mb-4">
        <Descriptions column={1}>
          <Descriptions.Item label="網絡">
            <Tag color={online ? 'green' : 'red'}>{online ? '在線' : '離線'}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="待同步項">{pending}</Descriptions.Item>

          <Descriptions.Item label="同步失敗項">
            {failed > 0 ? <Tag color="red">{failed} 筆需處理</Tag> : 0}
          </Descriptions.Item>

          <Descriptions.Item label="上次同步">
            {lastSyncAt?.toLocaleString('zh-TW') ?? '—'}
          </Descriptions.Item>
        </Descriptions>

        <Space className="mt-2">
          {/* 全量同步：需在線且有 pending 條目 */}
          <Button
            type="primary"
            loading={syncing}
            onClick={triggerSync}
            disabled={!online || pending === 0}
          >
            立即全量同步
          </Button>

          {/* 導出本地備份（離線也可導出） */}
          <Button onClick={exportBackup}>導出本地備份</Button>

          {/* 釋放設備（危險操作，需確認） */}
          <Button danger onClick={releaseDevice} disabled={!fingerprint}>
            釋放當前設備
          </Button>
        </Space>
      </Card>

      {/* ── 同步失敗項卡片：重試耗盡 / 409 衝突的條目，需人工重試或放棄 ── */}
      <Card title="同步失敗項" className="mb-4">
        <FailedOutboxList onChanged={() => void refreshCounts()} />
      </Card>

      {/* ── iOS 用戶提醒 ── */}
      <Card title="iOS 用戶提醒" type="inner">
        <Paragraph>
          如果您使用 iPad 或 iPhone，請務必在 Safari 中點擊「分享」→「加入主畫面」把本應用安裝為
          PWA。
          <br />
          否則 iOS Safari 在 <strong>7 天</strong>未訪問後會自動清理 IndexedDB
          資料，造成未同步的稽核記錄丟失。
        </Paragraph>
        <Paragraph type="secondary">
          安裝為 PWA 後，瀏覽器會保留持久化存儲授權，資料僅在用戶主動清除網站資料時才會消失。
        </Paragraph>
      </Card>
    </div>
  );
}
